import type { SheetGeometryCategoryStyle } from "building-types/views/planSets";
import type { LineStyle, TextStyle } from "building-types/views/Primitive2DOutput";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import {
  exteriorDimensionCategoryReplaceMap,
  foundationDimensionCategoryReplaceMap,
  interiorDimensionCategoryReplaceMap,
  SheetGeometryCategory,
  utilityDimensionCategoryReplaceMap,
  annotationDimensionCategoryReplaceMap,
  annotationAllLvlDimensionCategoryReplaceMap,
  roofOverhangDimensionCategoryReplaceMap,
} from "building-types/views/sheetGeometryCategory";
import colorsOld from "common/constants/colorsOld";
import { SVG_UNITS_PER_100TH_INCH } from "common/constants/pdfSettings";
import { ONE_POINT, ZERO_POINTS } from "common/constants/scaleFactors";
import { FontId } from "common/constants/textFont";
import {
  numMul,
  percentNew,
  sheetPixelsNew,
  sheetPixelsToInches,
  sheetPixelsToPoints,
  sheetPointsNew,
} from "common/utils/nominalNumbers";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { hatchProceduralMaterialNew } from "geom/hatchTypes";
import {
  CASEWORK_FONT_SIZE,
  DEFAULT_MEDIUM_FONT_SIZE,
  DEFAULT_REGULAR_FONT_SIZE,
  DEFAULT_SMALL_FONT_SIZE,
  DIM_FONT_SIZE,
  ROOM_LABEL_FONT_SIZE,
  SLAB_LABEL_FONT_SIZE,
  STANDARD_FONT_SIZE,
  TAG_FONT_SIZE,
} from "pdf/planFonts";
import {
  GRADE_LINE_STROKE_WIDTH,
  LW_BOLD,
  LW_EXTRA_THIN,
  LW_MEDIUM,
  LW_THIN,
  WALL_CUT_STROKE_WIDTH,
  DASHED_LW,
  DIM_LW,
  DIM_TICK_LW,
  STAIR_LW,
  CASEWORK_LINE_WIDTH,
} from "./constants";
import type { SheetGeometryCategoryStyleSheets } from "./SheetGeometrySourceStyleOverrideTable";

const colors = {
  black: "#222222",
  white: "#ffffff",
} as const;

// Elevation views for historical reasons have a different default manual note size.
export const PLAN_VIEW_MANUAL_ANNOT_SIZE = TAG_FONT_SIZE;

const FRAMING_DASH_PATTERN = [sheetPointsNew(10), sheetPointsNew(2), sheetPointsNew(3), sheetPointsNew(2)];

const defaultWallStyle: LineStyle = {
  linewidth: WALL_CUT_STROKE_WIDTH,
  fill: true,
  fillColor: colorsOld.stoneLightX1,
  fillOpacity: percentNew(1),
};

const defaultPropStyle: LineStyle = {
  linewidth: LW_EXTRA_THIN,
  strokeColor: colorsOld.black,
};

const defaultDimensionLineStyle: LineStyle = { linewidth: DIM_LW };

const defaultDimensionStyles: SheetGeometryCategoryStyleSheets = {
  [SheetGeometryCategory.DIM_WITNESS]: { line: defaultDimensionLineStyle },
  [SheetGeometryCategory.DIM_TICK]: { line: { ...defaultDimensionLineStyle, linewidth: DIM_TICK_LW } },
  [SheetGeometryCategory.DIM_LINEWORK]: { line: defaultDimensionLineStyle },
  [SheetGeometryCategory.DIM_LINE]: { line: defaultDimensionLineStyle },
  [SheetGeometryCategory.DIM_LABEL]: { text: { fontSize: DIM_FONT_SIZE } },
};

const defaultRailingStyle: LineStyle = {
  linewidth: LW_MEDIUM,
  strokeColor: colorsOld.black,
};

const defaultElevationDimensionStyle: SheetGeometryCategoryStyle = {
  line: {
    strokeColor: colorsOld.black,
  },
  text: {
    fontSize: DIM_FONT_SIZE,
    font: FontId.ArialNarrow,
    fontColor: colorsOld.black,
  },
};

const defaultElevationMarkerStyle: SheetGeometryCategoryStyle = {
  line: {
    linewidth: ONE_POINT,
    strokeColor: colors.black,
    fill: true,
    fillColor: colors.black,
    fillOpacity: percentNew(1),
  },
  text: {
    fontSize: DEFAULT_MEDIUM_FONT_SIZE,
    font: FontId.ArialNarrow,
    fontColor: colors.black,
  },
};

const defaultStairStyle: LineStyle = {
  linewidth: STAIR_LW,
  strokeColor: colorsOld.stoneDarkX1,
};

const defaultElevationWindowTagStyle: TextStyle = {
  font: FontId.ArialNarrow,
  fontColor: colorsOld.black,
  fontSize: numMul(sheetPixelsToInches(sheetPixelsNew(10.5)), SVG_UNITS_PER_100TH_INCH),
};

// All internal Higharc category styles should be defined here.
const defaultCategoryStyles: SheetGeometryCategoryStyleSheets = {
  [SheetGeometryCategory.PORCH]: {
    line: { linewidth: LW_MEDIUM },
  },
  [SheetGeometryCategory.PORCH_COLUMNS]: {
    line: {
      linewidth: LW_BOLD,
      fill: true,
      fillColor: colorsOld.stoneLightX2,
      fillOpacity: percentNew(1),
    },
  },
  [SheetGeometryCategory.PORCH_BEAMS]: {
    line: {
      linewidth: LW_THIN,
      dash: true,
    },
  },
  [SheetGeometryCategory.ELECTRICAL_LINKS]: {
    line: {
      linewidth: sheetPointsNew(0.25),
      fill: false,
      dash: true,
    },
  },
  [SheetGeometryCategory.DECK]: {
    line: {
      linewidth: LW_MEDIUM,
    },
  },
  [SheetGeometryCategory.FOUNDATION_DASHED_LINEWORK]: {
    line: { linewidth: DASHED_LW, fill: false, dash: true },
  },
  [SheetGeometryCategory.FOUNDATION_SOLID_LINEWORK]: {
    line: { linewidth: WALL_CUT_STROKE_WIDTH, fill: false },
  },
  [SheetGeometryCategory.CASEWORK_TAG]: {
    text: { fontSize: CASEWORK_FONT_SIZE },
  },
  [SheetGeometryCategory.CEILING]: {
    line: { dash: true, linewidth: LW_EXTRA_THIN, strokeColor: colorsOld.black },
  },
  [SheetGeometryCategory.CHASE_ROOMS]: {
    line: {
      linewidth: WALL_CUT_STROKE_WIDTH,
      fill: true,
      fillColor: colorsOld.white,
      fillOpacity: percentNew(1),
      strokeColor: colorsOld.black,
    },
  },
  [SheetGeometryCategory.COLUMN]: {
    line: { linewidth: LW_BOLD, fill: false },
  },
  [SheetGeometryCategory.STEM_WALL_UNIONED_EDGES]: {
    line: { linewidth: WALL_CUT_STROKE_WIDTH, fill: false },
  },
  [SheetGeometryCategory.STEM_WALL_ABOVE]: {
    line: { linewidth: LW_BOLD, fill: false, dash: true, dashLength: [sheetPointsNew(3), sheetPointsNew(1)] },
  },
  [SheetGeometryCategory.STEM_WALL_BELOW]: {
    line: { linewidth: LW_BOLD, fill: false },
  },
  [SheetGeometryCategory.SLAB_PATIO]: {
    line: { linewidth: LW_MEDIUM, strokeColor: colorsOld.black },
  },

  [SheetGeometryCategory.FLOOR_FINISH]: {
    line: { fillOpacity: percentNew(0.5) },
  },
  [SheetGeometryCategory.FOUNDATION_CURB]: {
    line: { linewidth: LW_THIN, fill: false, dash: false },
  },
  [SheetGeometryCategory.FOOTING]: {
    line: { linewidth: LW_THIN, fill: false, dash: true },
  },
  [SheetGeometryCategory.SLAB_ABOVE]: {
    line: { linewidth: LW_THIN, fill: false, dash: false },
  },
  [SheetGeometryCategory.SLAB_BELOW]: {
    line: { linewidth: LW_BOLD, fill: false, dash: false },
  },
  [SheetGeometryCategory.FOUNDATION_OUTLINE]: {
    line: { linewidth: LW_BOLD, fill: false, dash: true },
  },
  [SheetGeometryCategory.SLAB_TAG]: {
    text: { fontSize: SLAB_LABEL_FONT_SIZE },
  },
  [SheetGeometryCategory.DOORS]: {
    line: {
      linewidth: LW_EXTRA_THIN,
      strokeColor: colorsOld.black,
      fillColor: colorsOld.white,
      fillOpacity: percentNew(1),
    },
  },
  [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: {
    line: { linewidth: LW_EXTRA_THIN, strokeColor: colorsOld.black },
  },
  [SheetGeometryCategory.DOOR_TAGS]: {
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.HEADER_FRAMING_TAGS]: {
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.FLOOR_FRAMING]: {
    line: {
      linewidth: LW_MEDIUM,
      fill: false,
      dash: true,
      dashLength: FRAMING_DASH_PATTERN,
    },
  },
  [SheetGeometryCategory.FLOOR_FRAMING_LABEL]: {
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.FLOOR_SYSTEM_TAG]: {
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.ROOF_FRAMING]: {
    line: {
      linewidth: LW_MEDIUM,
      fill: false,
      dash: true,
      dashLength: FRAMING_DASH_PATTERN,
    },
  },
  [SheetGeometryCategory.PERIMS_FLOORPLATE]: {
    line: {
      linewidth: LW_EXTRA_THIN,
      fill: false,
      strokeColor: colorsOld.stoneDarkX1,
      dash: false,
    },
  },
  [SheetGeometryCategory.PERIMS_WALL]: {
    line: {
      linewidth: LW_EXTRA_THIN,
      fill: false,
      strokeColor: colorsOld.stoneDarkX1,
      dash: true,
    },
  },
  [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: {
    line: defaultPropStyle,
  },
  [SheetGeometryCategory.FLOOR_FILL]: {
    line: {
      fill: true,
      fillColor: "#e0e0e0",
      fillOpacity: percentNew(0.5),
    },
  },
  [SheetGeometryCategory.PROP_FILL]: {
    line: {
      fill: true,
      fillColor: "#ffffff",
      fillOpacity: percentNew(0.7),
    },
  },
  [SheetGeometryCategory.PROP_APPLIANCES]: {
    line: defaultPropStyle,
  },
  [SheetGeometryCategory.PROP_FIXTURES]: {
    line: defaultPropStyle,
  },
  [SheetGeometryCategory.PROP_FURNITURE]: {
    line: defaultPropStyle,
  },
  [SheetGeometryCategory.PROP_OTHER]: {
    line: defaultPropStyle,
  },
  [SheetGeometryCategory.PROP_TAG]: {
    // TODO: Relaxation of prop tags takes place in the generator function using TAG_FONT_SIZE
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.WALL_CORE]: {
    line: defaultWallStyle,
  },
  // TODO: Generator function dynamically sets hatching based on whether the finish cross section
  // layer type === WallMaterialType.EXTERIOR_FINISH
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH]: {
    // line: wallDefaults,
  },
  [SheetGeometryCategory.WALL_EXTERIOR_FINISH_PROJECTED]: {
    line: { linewidth: LW_EXTRA_THIN, strokeColor: colorsOld.stone },
  },
  [SheetGeometryCategory.WALL_BOUNDARIES]: {
    line: { ...defaultWallStyle, fill: undefined },
  },
  [SheetGeometryCategory.WALL_SUBSTRATE]: {
    line: { linewidth: LW_MEDIUM, fill: false, fillOpacity: percentNew(0) },
  },
  [SheetGeometryCategory.WINDOWS]: {
    line: { linewidth: LW_EXTRA_THIN, fill: false, strokeColor: colorsOld.black },
  },
  [SheetGeometryCategory.WINDOW_CASING_PROJECTED]: {
    line: { fill: true, fillColor: "#FFFFFF", linewidth: sheetPointsNew(0.005) },
  },
  // TODO: Generator function dynamically sets hatch based on whether there is a materialId
  [SheetGeometryCategory.WINDOW_CASING_CUT]: {
    line: {
      fill: true,
      fillColor: "#FFFFFF",
      hatchMaterial: hatchProceduralMaterialNew(HatchMaterialId.Diagonal_Lines),
      hatchLineWidth: sheetPointsNew(0.005),
      linewidth: sheetPointsNew(0.2),
    },
  },
  [SheetGeometryCategory.WINDOW_TAGS]: {
    text: { fontSize: TAG_FONT_SIZE },
  },
  [SheetGeometryCategory.STAIR_WALLS]: {
    line: {
      linewidth: WALL_CUT_STROKE_WIDTH,
      fill: true,
      fillColor: colorsOld.stoneLightX1,
      fillOpacity: percentNew(1),
    },
  },
  [SheetGeometryCategory.STAIR_PROJECTED]: {
    line: { dash: false, ...defaultStairStyle },
  },
  [SheetGeometryCategory.STAIR_ABOVE]: { line: { dash: true, ...defaultStairStyle } },
  [SheetGeometryCategory.STAIR_BREAK_LINE]: { line: { dash: false, ...defaultStairStyle } },
  [SheetGeometryCategory.FLOOR_AREA_CONDITIONED]: {
    line: { fillColor: colorsOld.stoneLight, fillOpacity: percentNew(1) },
  },
  [SheetGeometryCategory.FLOOR_AREA_UNCONDITIONED]: {
    line: { fillColor: colorsOld.stoneLightX2, fillOpacity: percentNew(1) },
  },
  [SheetGeometryCategory.PIER]: { line: { linewidth: LW_BOLD, fill: false, dash: false } },
  [SheetGeometryCategory.PIER_HIDDEN]: { line: { linewidth: LW_BOLD, fill: false, dash: true } },
  [SheetGeometryCategory.ROOF_DORMER_FRAMING]: {
    line: { linewidth: ONE_POINT, fill: false, dash: true, strokeColor: "black" },
  },
  [SheetGeometryCategory.ROOF_PERIMS]: {
    line: { linewidth: sheetPointsNew(0.5), fill: false, dash: true, dashMask: true },
  },
  [SheetGeometryCategory.ANNOT_NOTE]: {
    // line style for note leader
    line: { strokeColor: colorsOld.black, fillColor: colorsOld.black, linewidth: LW_EXTRA_THIN },
  },
  ...defaultDimensionStyles,
  ...makeDimensionStyles(exteriorDimensionCategoryReplaceMap),
  ...makeDimensionStyles(interiorDimensionCategoryReplaceMap),
  ...makeDimensionStyles(foundationDimensionCategoryReplaceMap),
  ...makeDimensionStyles(utilityDimensionCategoryReplaceMap),
  ...makeDimensionStyles(annotationDimensionCategoryReplaceMap),
  ...makeDimensionStyles(annotationAllLvlDimensionCategoryReplaceMap),
  ...makeDimensionStyles(roofOverhangDimensionCategoryReplaceMap),
  [SheetGeometryCategory.WAINSCOTING_CUT]: { line: { linewidth: LW_MEDIUM } },
  [SheetGeometryCategory.WAINSCOTING_PROJECTED]: { line: { linewidth: LW_EXTRA_THIN, strokeColor: colorsOld.stone } },
  [SheetGeometryCategory.RAILING_PROJECTED]: { line: defaultRailingStyle },
  [SheetGeometryCategory.RAILING_ABOVE]: { line: { dash: true, ...defaultRailingStyle } },
  [SheetGeometryCategory.RAILING_PROJECTED_STAIR_ONLY]: { line: defaultRailingStyle },
  [SheetGeometryCategory.RAILING_ABOVE_STAIR_ONLY]: { line: { dash: true, ...defaultRailingStyle } },
  [SheetGeometryCategory.RAILING_POST]: { line: defaultRailingStyle },
  [SheetGeometryCategory.CASEWORK]: { line: { linewidth: CASEWORK_LINE_WIDTH } },
  [SheetGeometryCategory.BRICKLEDGE_CUT]: {
    line: { linewidth: LW_MEDIUM },
  },
  [SheetGeometryCategory.BRICKLEDGE_PROJECTED]: {
    line: { linewidth: LW_BOLD },
  },
  [SheetGeometryCategory.MATERIAL_TAGS]: {
    text: { fontSize: STANDARD_FONT_SIZE, fontColor: colorsOld.black },
    line: {
      linewidth: sheetPixelsToPoints(sheetPixelsNew(1)),
      strokeColor: colorsOld.black,
      fillColor: colorsOld.black,
    },
  },
  [SheetGeometryCategory.DORMER_WINDOW_VERTICAL_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.ROOF_TRUNCATION_HEIGHT_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.ROOF_HORIZONTAL_PLACEMENT_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.ROOF_WIDTH_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_VERTICAL_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.ROOF_SLOPE_SYMBOL]: {
    line: {
      fill: true,
      fillOpacity: percentNew(1),
      fillColor: colors.white,
      linewidth: ONE_POINT,
      strokeColor: colors.black,
    },
    text: {
      fontSize: DEFAULT_REGULAR_FONT_SIZE,
      font: FontId.ArialNarrow,
      fontColor: colors.black,
    },
  },
  [SheetGeometryCategory.ROOF_SLOPE_ANNOS]: {
    text: {
      fontSize: DEFAULT_SMALL_FONT_SIZE,
    },
  },
  [SheetGeometryCategory.ROOF_SKYLIGHT_ANNOS]: {
    text: {
      fontSize: DEFAULT_REGULAR_FONT_SIZE,
    },
  },
  [SheetGeometryCategory.ELEVATION_MARKERS]: defaultElevationMarkerStyle,
  [SheetGeometryCategory.TOP_OF_PLATE_MARKERS]: defaultElevationMarkerStyle,
  [SheetGeometryCategory.TOP_OF_PLATE_DIMENSIONS]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.WINDOW_HEADER_ANNOTATION]: defaultElevationDimensionStyle,
  [SheetGeometryCategory.DORMER_WINDOW_TAGS]: {
    text: defaultElevationWindowTagStyle,
  },
  [SheetGeometryCategory.ROOF_GABLE_WINDOW_TAGS]: {
    text: defaultElevationWindowTagStyle,
  },
  // Registered as SheetGeometryStyle.LINE in sheetGeometryStyleMap, so these are stroke properties
  // rather than fill. A View Template's Grade Line style controls override them.
  [SheetGeometryCategory.GRADE_LINE]: {
    line: { linewidth: GRADE_LINE_STROKE_WIDTH, strokeColor: colorsOld.black, fill: false },
  },
  [SheetGeometryCategory.ROOF_PERIMS_WITH_LEVEL_PERIMS]: {
    line: { linewidth: sheetPointsNew(0.5), fill: false, dash: true },
  },
  [SheetGeometryCategory.UTILITY_FOUNDATION_LINEWORK]: { line: { linewidth: WALL_CUT_STROKE_WIDTH } },
  [SheetGeometryCategory.STEM_WALL_ABOVE_GAPS]: {
    line: { linewidth: LW_MEDIUM, dashLength: [sheetPointsNew(3), sheetPointsNew(1)] },
  },
  [SheetGeometryCategory.CONDITION_TAG]: {
    line: { linewidth: LW_MEDIUM },
  },
  [SheetGeometryCategory.ROOM_LABELS]: {
    text: { fontSize: ROOM_LABEL_FONT_SIZE },
  },
  [SheetGeometryCategory.ELECTRIC_LOAD_LABEL]: {
    text: { fontSize: DEFAULT_SMALL_FONT_SIZE },
    // line style for the load-label leader (see buildAnchorPointLeaderDefinition); a defensive
    // default in case a leader is ever built here without an explicit color, same as ANNOT_NOTE.
    line: { strokeColor: colors.black, fillColor: colors.black, linewidth: LW_MEDIUM },
  },
};

export const elevationDefaultCategoryStyles: SheetGeometryCategoryStyleSheets = {
  ...defaultCategoryStyles,
  [SheetGeometryCategory.ANNOT_DIM_LABEL]: {
    text: {
      fontSize: PLAN_VIEW_MANUAL_ANNOT_SIZE,
      font: FontId.ArialNarrow,
    },
  },
  [SheetGeometryCategory.ANNOT_NOTE]: {
    text: {
      fontSize: PLAN_VIEW_MANUAL_ANNOT_SIZE,
      font: FontId.ArialNarrow,
    },
  },
};

export const planDefaultCategoryStyles: SheetGeometryCategoryStyleSheets = {
  ...defaultCategoryStyles,
  [SheetGeometryCategory.ANNOT_NOTE]: {
    text: {
      fontSize: PLAN_VIEW_MANUAL_ANNOT_SIZE,
      font: FontId.ArialNarrow,
    },
  },
  [SheetGeometryCategory.ALL_LVL_ANNOT_NOTE]: {
    text: {
      fontSize: PLAN_VIEW_MANUAL_ANNOT_SIZE,
      font: FontId.ArialNarrow,
    },
  },
};

function makeDimensionStyles(
  categoryReplaceMap: SheetGeometryCategoryTable<SheetGeometryCategory>,
): SheetGeometryCategoryStyleSheets {
  const newStyles = {};

  Object.keys(defaultDimensionStyles).forEach((baseCat) => {
    const style = defaultDimensionStyles[baseCat];
    const category = categoryReplaceMap[baseCat];
    if (category) {
      newStyles[category] = style;
    }
  });

  return newStyles;
}

// These styles are applied to sheet geometry when they are generated. They are not
// meant to be overridden by the user and are typically used for things like masking.

export const wainscotingMaskInlineStyle: LineStyle = {
  linewidth: ZERO_POINTS,
  strokeOpacity: percentNew(0),
  fill: true,
  fillColor: colorsOld.white,
  fillOpacity: percentNew(1),
};

export const wainscotingHatchInlineStyle: LineStyle = {
  linewidth: ZERO_POINTS,
  fill: false,
  dash: false,
  fillOpacity: percentNew(0.075),
  hatch: true,
  hatchStroke: false,
  hatchLineWidth: sheetPointsNew(0.25),
  hatchStrokeColor: colorsOld.stoneDark,
  hatchMaterial: hatchProceduralMaterialNew(HatchMaterialId.Diagonal_Lines),
};

export const railingMaskInlineStyle: LineStyle = {
  fill: true,
  fillColor: colorsOld.white,
  fillOpacity: percentNew(1),
};

export const wallExteriorFinishMaskInlineStyle: LineStyle = {
  linewidth: ZERO_POINTS,
  strokeOpacity: percentNew(0),
  fill: true,
  fillColor: colorsOld.white,
  fillOpacity: percentNew(1),
};
